import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store';
import Btn from '../ui/Btn';
import Chip from '../ui/Chip';
import Panel from '../ui/Panel';
import Spinner from '../ui/Spinner';
import { Field, Input } from '../ui/Field';
import Crosshair from '../brand/Crosshair';
import PremierBadge from '../cs/PremierBadge';
import { containsProfanity } from '../utils/profanity';
import PhoneInput, { isValidKzPhone, rawPhone } from '../ui/PhoneInput';

function Stepper({ step, steps }) {
  return (
    <div className="row" style={{ gap: 0, marginBottom: 36 }}>
      {steps.map((s, i) => {
        const done = i < step, cur = i === step;
        return (
          <React.Fragment key={i}>
            <div className="col" style={{ alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <div className="center" style={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14,
                background: cur ? 'linear-gradient(120deg, var(--accent), var(--accent-deep))' : done ? 'rgba(74,222,128,0.15)' : 'var(--surface-2)',
                color: cur ? '#fff' : done ? 'var(--ok)' : 'var(--fg-muted)',
                border: `1px solid ${cur ? 'var(--accent)' : done ? 'var(--ok)' : 'var(--line)'}`,
                boxShadow: cur ? '0 0 18px -4px var(--accent-glow)' : 'none', transition: 'all .3s var(--ease-out)',
              }}>
                {done
                  ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  : i + 1}
              </div>
              <span className="mono step-label" style={{ fontSize: 10, letterSpacing: '0.08em', color: cur ? 'var(--fg)' : 'var(--fg-muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{s}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, margin: '0 6px', marginTop: 18, alignSelf: 'flex-start', background: i < step ? 'linear-gradient(90deg, var(--ok), var(--accent))' : 'var(--line)', transition: 'background .4s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FileDrop({ file, onFile, t }) {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null);
  const handle = (fl) => {
    if (!fl) return;
    onFile(fl);
    if (fl.type && fl.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(fl));
    } else {
      setPreview(null);
    }
  };
  return (
    <div
      onClick={() => ref.current.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files[0]); }}
      style={{ position: 'relative', cursor: 'pointer', borderRadius: 'var(--r-lg)', overflow: 'hidden', border: `2px dashed ${drag ? 'var(--accent)' : file ? 'var(--ok)' : 'var(--line-strong)'}`, background: drag ? 'rgba(225,18,23,0.08)' : 'var(--surface)', transition: 'all .2s', padding: file ? 0 : '36px 24px', minHeight: file ? 0 : 180 }}
    >
      <input ref={ref} type="file" accept="image/*,.pdf" onChange={e => handle(e.target.files[0])}
        onClick={e => e.stopPropagation()}
        style={file ? { display: 'none' } : { position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 1 }} />
      {!file ? (
        <div className="col center" style={{ gap: 12, textAlign: 'center' }}>
          <div className="center" style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(225,18,23,0.12)', border: '1px solid var(--line-accent)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-bright)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V4m0 0L7 9m5-5l5 5" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></svg>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{t ? t('register.dropzoneTitle') : 'Перетащи фото или нажми, чтобы выбрать'}</div>
            <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 5 }}>{t ? t('register.dropzoneHint') : 'Студенческий билет / справка · JPG, PNG, PDF · до 10 МБ'}</div>
          </div>
        </div>
      ) : (
        <div className="row" style={{ gap: 14, padding: 14, alignItems: 'center' }}>
          <div className="center" style={{ width: 72, height: 72, borderRadius: 'var(--r-md)', overflow: 'hidden', flexShrink: 0, background: 'var(--surface-3)', border: '1px solid var(--line)' }}>
            {preview
              ? <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>}
          </div>
          <div className="col grow" style={{ minWidth: 0 }}>
            <div className="row gap-2" style={{ alignItems: 'center' }}>
              <span style={{ color: 'var(--ok)' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg></span>
              <span style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
            </div>
            <span className="mono" style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 3 }}>{(file.size / 1024).toFixed(0)} {t ? t('register.kbUploaded') : 'КБ · загружено'}</span>
          </div>
          <button onClick={e => { e.stopPropagation(); onFile(null); setPreview(null); }} style={{ background: 'var(--surface-3)', border: '1px solid var(--line)', color: 'var(--fg-muted)', width: 34, height: 34, borderRadius: 8, cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>
      )}
    </div>
  );
}

function SuccessScreen({ data, go, teamRequested, isAnonymous, t }) {
  return (
    <div className="center page-in" style={{ flexDirection: 'column', textAlign: 'center', padding: '40px 20px', minHeight: '60vh' }}>
      <div style={{ position: 'relative', marginBottom: 28 }}>
        <div style={{ position: 'absolute', inset: '-40px', background: 'radial-gradient(circle, rgba(74,222,128,0.25), transparent 70%)', filter: 'blur(20px)' }} />
        <div className="center" style={{ position: 'relative', width: 110, height: 110, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(74,222,128,0.2), var(--surface))', border: '2px solid var(--ok)', animation: 'popIn .5s var(--ease-spring) both' }}>
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="var(--ok)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ strokeDasharray: 40, strokeDashoffset: 40, animation: 'drawCheck .6s .3s var(--ease-out) forwards' }}><path d="M20 6L9 17l-5-5" /></svg>
        </div>
        <style>{`@keyframes drawCheck{to{stroke-dashoffset:0}}`}</style>
      </div>
      <div className="mono" style={{ fontSize: 12, letterSpacing: '0.25em', color: 'var(--ok)', marginBottom: 12 }}>● {t('register.successBadge')}</div>
      <h1 className="display" style={{ fontSize: 'clamp(30px,5vw,48px)', margin: '0 0 12px' }}>{t('register.alreadyIn')}<span className="grad-red">{data.nick || 'игрок'}</span></h1>
      <p style={{ color: 'var(--fg-soft)', maxWidth: 460, margin: '0 auto 8px', fontSize: 16, lineHeight: 1.55 }}>
        {t('register.successDesc')}
      </p>
      {isAnonymous
        ? <p className="muted" style={{ fontSize: 14, maxWidth: 440, margin: '0 auto 28px' }}>{t('register.successAnon')}</p>
        : teamRequested
          ? <p className="muted" style={{ fontSize: 14, maxWidth: 440, margin: '0 auto 28px' }}>{t('register.successTeamSent', { team: teamRequested })}</p>
          : <p className="muted" style={{ fontSize: 14, maxWidth: 440, margin: '0 auto 28px' }}>{t('register.successNoTeam')}</p>
      }
      <div className="row gap-3 wrap" style={{ justifyContent: 'center' }}>
        {isAnonymous
          ? <Btn size="lg" onClick={() => go('signup')}>{t('nav.signup')}</Btn>
          : <Btn size="lg" onClick={() => go('profile')}>{t('nav.profile')}</Btn>
        }
        <Btn size="lg" variant="ghost" onClick={() => go('home')}>{t('nav.home')}</Btn>
      </div>
      <div className="row gap-3 wrap" style={{ justifyContent: 'center', marginTop: 34 }}>
        <Chip color="var(--ok)" icon="✓">{t('register.chipFio')}</Chip>
        <Chip color="var(--ok)" icon="✓">{t('register.chipSteam')}</Chip>
        <Chip color="var(--ok)" icon="✓">{t('register.chipDoc')}</Chip>
        <Chip color="var(--warn)">{t('register.chipModeration')}</Chip>
        {teamRequested && <Chip color="var(--info)" icon="⏳">{t('register.chipTeamReq')}</Chip>}
      </div>
    </div>
  );
}

export default function RegisterPage() {
  const { go, user, submitTournamentApplication, fetchTeams, requestJoinTeam } = useAppStore();
  const { t } = useTranslation();

  const STEPS = t('register.steps', { returnObjects: true });
  const ROLES = [
    { k: 'igl',     l: t('register.roles.igl.l'),     d: t('register.roles.igl.d') },
    { k: 'awp',     l: t('register.roles.awp.l'),     d: t('register.roles.awp.d') },
    { k: 'entry',   l: t('register.roles.entry.l'),   d: t('register.roles.entry.d') },
    { k: 'support', l: t('register.roles.support.l'), d: t('register.roles.support.d') },
    { k: 'lurk',    l: t('register.roles.lurk.l'),    d: t('register.roles.lurk.d') },
  ];

  useEffect(() => {
    if (user?.user_type === 'admin') {
      go('admin');
    } else if (user?.status && user.status !== 'none') {
      go('profile');
    }
  }, [user, go]);

  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [teamRequested, setTeamRequested] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState({});
  const [d, setD] = useState({
    fio: '', phone: '', nick: user?.nickname || '', steam: '', rating: '', role: 'entry',
    edu: '', eduType: t('register.student'), grade: '', file: null, agree: false,
  });
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (step === 4) {
      setLoadingTeams(true);
      fetchTeams('recruiting').then(data => {
        setTeams(data || []);
        setLoadingTeams(false);
      });
    }
  }, [step, fetchTeams]);

  const set = (k, v) => { setD(s => ({ ...s, [k]: v })); setErr(e => ({ ...e, [k]: null })); };
  const steamOk = /steamcommunity\.com\//i.test(d.steam);

  const validate = (s) => {
    const e = {};
    if (s === 0) { if (d.fio.trim().split(' ').length < 2) e.fio = t('register.errFioFull'); else if (d.fio.trim().split(' ').some(w => containsProfanity(w))) e.fio = t('register.errFioClean'); if (!isValidKzPhone(d.phone)) e.phone = t('register.errPhone'); }
    if (s === 1) { if (!d.nick.trim()) e.nick = t('register.errNick'); else if (containsProfanity(d.nick)) e.nick = t('register.errNickBad'); if (!steamOk) e.steam = t('register.errSteam'); }
    if (s === 2) { if (!d.edu.trim()) e.edu = t('register.errEdu'); if (!d.grade.trim()) e.grade = t('register.errGrade'); if (!d.file) e.file = 'doc'; }
    setErr(e); return Object.keys(e).length === 0;
  };
  const next = () => { if (!validate(step)) return; if (step < 3) setStep(step + 1); };
  const back = () => step > 0 ? setStep(step - 1) : go('home');
  const submit = async () => {
    if (!d.agree) { setErr(e => ({ ...e, agree: t('register.errAgree') })); return; }
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append('fio', d.fio);
      formData.append('game_nick', d.nick);
      formData.append('phone', rawPhone(d.phone));
      formData.append('steam_profile', d.steam);
      formData.append('premier_rating', d.rating || 0);
      formData.append('role', d.role);
      formData.append('edu_type', d.eduType === t('register.student') ? 'student' : 'school');
      formData.append('education_name', d.edu);
      formData.append('grade', d.grade);
      if (d.file) formData.append('document', d.file);

      const result = await submitTournamentApplication(formData);
      if (!localStorage.getItem('access_token') && result?.application?.id) {
        localStorage.setItem('pending_app_id', result.application.id);
      }
      setStep(4);
    } catch (e) {
      setErr(ev => ({ ...ev, _global: e.message }));
    } finally {
      setBusy(false);
    }
  };

  const sendRequest = async () => {
    if (!selectedTeam) return;
    setRequesting(true);
    try {
      await requestJoinTeam(selectedTeam.id);
      setTeamRequested(selectedTeam.name);
      setDone(true);
    } catch (e) {
      console.error(e);
    } finally {
      setRequesting(false);
    }
  };

  if (done) return <SuccessScreen data={d} go={go} teamRequested={teamRequested} isAnonymous={!user} t={t} />;

  const isStudent = d.eduType === t('register.student');

  return (
    <div className="page-in" style={{ position: 'relative', zIndex: 1, padding: '40px 0 80px' }}>
      <div className="container" style={{ maxWidth: 760 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
          <div className="eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Crosshair style={{ width: 16 }} />{t('register.eyebrow')}
          </div>
          <Chip color="var(--accent)">{t('register.chipIndividual')}</Chip>
        </div>
        <h1 className="display" style={{ fontSize: 'clamp(26px,4vw,38px)', margin: '0 0 28px' }}>{t('register.pageTitle')}</h1>

        <Panel pad={0} glow style={{ overflow: 'hidden' }}>
          <div style={{ position: 'relative', height: 4, background: 'var(--surface-3)', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, width: `${((step + 1) / 4) * 100}%`, background: 'linear-gradient(90deg, var(--accent-deep), var(--accent-bright))', transition: 'width .5s var(--ease-out)', boxShadow: '0 0 14px var(--accent-glow)' }} />
          </div>
          <div style={{ padding: 'clamp(22px, 4vw, 36px)' }}>
            <Stepper step={step} steps={Array.isArray(STEPS) ? STEPS : []} />

            <div key={step} style={{ animation: 'fadeUp 0.42s var(--ease-out) both' }}>
              {/* STEP 0 */}
              {step === 0 && (
                <div className="col gap-5">
                  <Field label={t('register.fio')} required hint={t('register.fioHint')} error={err.fio} icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>}>
                    <Input placeholder="Сейтжанов Алихан Маратович" value={d.fio} error={err.fio} icon onChange={e => set('fio', e.target.value)} />
                  </Field>
                  <Field label={t('register.phone')} required hint={t('register.phoneHint')} error={err.phone} valid={isValidKzPhone(d.phone)} icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.4-1.4a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" /></svg>}>
                    <PhoneInput value={d.phone} error={err.phone} onChange={v => set('phone', v)} />
                  </Field>
                </div>
              )}

              {/* STEP 1 */}
              {step === 1 && (
                <div className="col gap-5">
                  <Field label={t('register.nickname')} required error={err.nick} icon={<Crosshair style={{ width: 16 }} color="var(--fg-muted)" />}>
                    <Input placeholder="s1lent" value={d.nick} error={err.nick} icon onChange={e => set('nick', e.target.value)} />
                  </Field>
                  <Field label={t('register.steam')} required error={err.steam} valid={steamOk} hint="steamcommunity.com/id/... или /profiles/..." icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 0-9.9 8.7l5.3 2.2a2.8 2.8 0 0 1 1.6-.5l2.4-3.4a3.7 3.7 0 1 1 3.7 3.7l-3.4 2.4a2.8 2.8 0 0 1-5.5.8L2 14.9A10 10 0 1 0 12 2z" /></svg>}>
                    <Input placeholder="https://steamcommunity.com/id/..." value={d.steam} error={err.steam} icon onChange={e => set('steam', e.target.value)} />
                  </Field>
                  <Field label={t('register.ratingLabel')} hint={t('register.ratingHint')}>
                    <div className="row gap-3" style={{ alignItems: 'center' }}>
                      <Input type="number" placeholder="напр. 18500" value={d.rating} onChange={e => set('rating', e.target.value)} style={{ maxWidth: 200 }} />
                      {d.rating && Number(d.rating) > 0 && <PremierBadge rating={Number(d.rating)} />}
                    </div>
                  </Field>
                  <div>
                    <span className="mono" style={{ fontSize: 11, letterSpacing: '0.16em', color: 'var(--fg-muted)', textTransform: 'uppercase' }}>{t('register.role')}</span>
                    <div className="row gap-2 wrap" style={{ marginTop: 12 }}>
                      {ROLES.map(r => (
                        <button key={r.k} onClick={() => set('role', r.k)} style={{
                          textAlign: 'left', padding: '11px 14px', borderRadius: 'var(--r-md)', cursor: 'pointer', minWidth: 110,
                          background: d.role === r.k ? 'rgba(225,18,23,0.12)' : 'var(--surface)',
                          border: `1px solid ${d.role === r.k ? 'var(--accent)' : 'var(--line)'}`, transition: 'all .18s',
                        }}>
                          <div className="display" style={{ fontSize: 15, color: d.role === r.k ? 'var(--accent-bright)' : 'var(--fg)' }}>{r.l}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2 }}>{r.d}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                <div className="col gap-5">
                  <div className="row gap-2" style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', padding: 4, width: 'fit-content' }}>
                    {[t('register.student'), t('register.schoolboy')].map(tp => (
                      <button key={tp} onClick={() => set('eduType', tp)} style={{ padding: '8px 18px', borderRadius: 'var(--r-sm)', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.04em', background: d.eduType === tp ? 'linear-gradient(120deg, var(--accent), var(--accent-deep))' : 'transparent', color: d.eduType === tp ? '#fff' : 'var(--fg-muted)', transition: 'all .2s' }}>{tp}</button>
                    ))}
                  </div>
                  <div className="row gap-3 field-row-2col" style={{ alignItems: 'flex-start' }}>
                    <Field label={isStudent ? t('register.university') : t('register.school')} required error={err.edu}>
                      <Input placeholder={isStudent ? t('register.uni_placeholder') : t('register.school_placeholder')} value={d.edu} error={err.edu} onChange={e => set('edu', e.target.value)} />
                    </Field>
                    <Field label={isStudent ? t('register.grade_student') : t('register.grade_school')} required error={err.grade} valid={!!d.grade}>
                      <Input placeholder={isStudent ? t('register.grade_placeholder_student') : t('register.grade_placeholder_school')} value={d.grade} error={err.grade} onChange={e => set('grade', e.target.value)} />
                    </Field>
                  </div>
                  <Field label={t('register.document')} required error={err.file === 'doc' ? t('register.errFile') : null}>
                    <div style={{ width: '100%' }}><FileDrop file={d.file} onFile={f => set('file', f)} t={t} /></div>
                  </Field>
                  <div className="row gap-2" style={{ padding: '12px 14px', background: 'rgba(88,166,255,0.08)', border: '1px solid rgba(88,166,255,0.25)', borderRadius: 'var(--r-md)' }}>
                    <span style={{ color: 'var(--info)', flexShrink: 0, marginTop: 1 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4m0-4h.01" /></svg></span>
                    <span style={{ fontSize: 12.5, color: 'var(--fg-soft)', lineHeight: 1.5 }}>{t('register.docPrivacy')}</span>
                  </div>
                </div>
              )}

              {/* STEP 3 — review */}
              {step === 3 && (
                <div className="col gap-4">
                  <div className="mono" style={{ fontSize: 11, letterSpacing: '0.18em', color: 'var(--accent)', textTransform: 'uppercase' }}>{t('register.reviewHeader')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
                    {[
                      [t('register.fio'), d.fio, 0],
                      [t('register.phone'), d.phone, 0],
                      [t('register.nickname'), d.nick, 1],
                      [t('register.steam'), d.steam, 1],
                      [isStudent ? t('register.university') : t('register.school'), `${d.edu} · ${d.grade}`, 2],
                      [t('register.docReview'), d.file ? d.file.name : '—', 2],
                    ].map(([l, v, s], i) => (
                      <div key={i} style={{ padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', position: 'relative' }}>
                        <div className="row" style={{ justifyContent: 'space-between' }}>
                          <span className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', color: 'var(--fg-muted)', textTransform: 'uppercase' }}>{l}</span>
                          <button onClick={() => setStep(s)} style={{ background: 'none', border: 'none', color: 'var(--accent-bright)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>{t('common.edit')}</button>
                        </div>
                        <div style={{ fontSize: 14, marginTop: 6, fontWeight: 500, wordBreak: 'break-word' }}>{v || '—'}</div>
                      </div>
                    ))}
                  </div>
                  <div className="row gap-3" style={{ padding: '14px 16px', background: 'var(--surface)', border: `1px solid ${err.agree ? 'var(--danger)' : 'var(--line)'}`, borderRadius: 'var(--r-md)', cursor: 'pointer' }} onClick={() => set('agree', !d.agree)}>
                    <div className="center" style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, background: d.agree ? 'var(--accent)' : 'transparent', border: `1.5px solid ${d.agree ? 'var(--accent)' : 'var(--line-strong)'}`, transition: 'all .15s' }}>
                      {d.agree && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>}
                    </div>
                    <span style={{ fontSize: 13.5, color: 'var(--fg-soft)', lineHeight: 1.4 }}>{t('register.agree')}</span>
                  </div>
                </div>
              )}

              {/* STEP 4 — команда */}
              {step === 4 && (
                <div className="col gap-4">
                  <div className="mono" style={{ fontSize: 11, letterSpacing: '0.18em', color: 'var(--accent)', textTransform: 'uppercase' }}>
                    {t('register.step4Badge')}
                  </div>
                  <p style={{ margin: 0, fontSize: 14, color: 'var(--fg-soft)', lineHeight: 1.55 }}>
                    {t('register.step4Desc')}
                  </p>
                  {loadingTeams ? (
                    <div className="center" style={{ padding: 40 }}><Spinner size={28} /></div>
                  ) : teams.length === 0 ? (
                    <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--fg-muted)', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
                      {t('register.noRecruitingTeams')}
                    </div>
                  ) : (
                    <div className="col gap-3" style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 2 }}>
                      {teams.map(tm => {
                        const sel = selectedTeam?.id === tm.id;
                        return (
                          <div key={tm.id} onClick={() => setSelectedTeam(sel ? null : tm)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderRadius: 'var(--r-md)', cursor: 'pointer', background: sel ? 'rgba(225,18,23,0.08)' : 'var(--surface)', border: `1px solid ${sel ? 'var(--accent)' : 'var(--line)'}`, transition: 'all .18s' }}>
                            <div className="row gap-3" style={{ alignItems: 'center' }}>
                              <div className="center display" style={{ width: 46, height: 46, borderRadius: 'var(--r-sm)', background: sel ? 'rgba(225,18,23,0.18)' : 'var(--surface-2)', fontSize: 13, border: `1px solid ${sel ? 'var(--accent)' : 'var(--line)'}`, color: sel ? 'var(--accent-bright)' : 'var(--fg)', flexShrink: 0 }}>{tm.tag}</div>
                              <div className="col">
                                <span style={{ fontWeight: 600, fontSize: 15 }}>{tm.name}</span>
                                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
                                  {tm.players_count || tm.members?.length || 0}/5 {t('common.players').toLowerCase()} · {t('register.teamSlotsCap')} {tm.captain?.nickname || '—'}
                                </span>
                              </div>
                            </div>
                            <div className="center" style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, border: `2px solid ${sel ? 'var(--accent)' : 'var(--line-strong)'}`, background: sel ? 'var(--accent)' : 'transparent', transition: 'all .15s' }}>
                              {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="row" style={{ justifyContent: 'space-between', marginTop: 32, gap: 12 }}>
              <Btn variant="ghost" onClick={back}>{step === 0 ? t('register.cancelBtn') : t('register.backBtn')}</Btn>
              {step < 3
                ? <Btn onClick={next} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4"><path d="M5 12h14m-6-6l6 6-6 6" /></svg>}>{t('register.nextBtn')}</Btn>
                : step === 3
                ? <Btn variant="gold" onClick={submit} disabled={busy} icon={busy ? <Spinner size={18} /> : null}>{busy ? t('common.sending') : t('register.submitBtn')}</Btn>
                : (
                  <div className="row gap-3">
                    <Btn variant="ghost" onClick={() => setDone(true)}>{t('register.skipBtn')}</Btn>
                    <Btn variant="gold" onClick={sendRequest} disabled={!selectedTeam || requesting} icon={requesting ? <Spinner size={18} /> : null}>
                      {requesting ? t('common.sending') : t('register.sendRequest')}
                    </Btn>
                  </div>
                )
              }
            </div>
          </div>
        </Panel>

        <p className="center" style={{ marginTop: 20, fontSize: 12.5, color: 'var(--fg-faint)', gap: 8 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
          {t('register.privacy')}
        </p>
      </div>
    </div>
  );
}
